import type { FC, FormEvent } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { ApolloError } from 'apollo-client';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/core/migration/search';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { fg } from '@confluence/feature-gating';
import type { PageCardAppearance } from '@confluence/page-card';
import { PageCardAppearanceToggle } from '@confluence/page-card';
import { ErrorDisplay } from '@confluence/error-boundary';

import { useSelectedView } from '../../useSelectedView';

import type { PillProps } from './Pills';
import { Pills } from './Pills';

const i18n = defineMessages({
	textFilterPlaceholder: {
		id: 'home.views.recent.options.placeholder',
		defaultMessage: 'Filter by title',
		description: 'Placeholder text for an input to filter pages by title',
	},
	textFilterDescription: {
		id: 'home.views.recent.options.filter.description',
		defaultMessage: 'Results will update as you type.',
		description: 'Aria description for content tree search input',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const OptionsContainer = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: ${token('space.400', '32px')};
`;

const optionsContainerStyles = css({
	display: 'flex',
	justifyContent: 'space-between',
	marginBottom: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const OptionsLeft = styled.div`
	display: flex;
`;

const optionsLeftStyles = css({
	display: 'flex',
});

type FilterTextProps = {
	filterText: string;
	setFilterText: (filterText: string) => void;
};

type AppearanceProps = {
	appearance: PageCardAppearance;
	setAppearance: (appearance: PageCardAppearance) => void;
};

type UserPreferenceProps = {
	loading: boolean;
	queryError?: ApolloError;
	mutationError?: ApolloError;
};

export type OptionsProps = FilterTextProps & PillProps & AppearanceProps & UserPreferenceProps;

export const Options: FC<OptionsProps> = ({
	appearance,
	setAppearance,
	filterText,
	setFilterText,
	pill,
	setPill,
	pills,
	loading,
	queryError,
	mutationError,
}) => {
	const { formatMessage } = useIntl();
	const selectedView = useSelectedView();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onSearchChange = useCallback(
		(e: FormEvent<HTMLInputElement>) => {
			const text = e.currentTarget.value;
			setFilterText(text);
		},
		[setFilterText],
	);

	const onClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${selectedView}Screen`,
				action: 'clicked',
				actionSubject: 'textField',
				actionSubjectId: `${selectedView}ScreenSearchBar`,
			},
		}).fire();
	}, [createAnalyticsEvent, selectedView]);

	const placeholder = formatMessage(i18n.textFilterPlaceholder);

	if (fg('confluence_frontend_home_compiled_migration')) {
		return (
			<div css={optionsContainerStyles}>
				<div css={optionsLeftStyles}>
					<Textfield
						placeholder={placeholder}
						aria-label={placeholder}
						defaultValue={filterText}
						isCompact
						width="medium"
						onChange={onSearchChange}
						onClick={onClick}
						elemBeforeInput={<SearchIcon label="" spacing="spacious" />}
						aria-describedby="filterByTitleDescription"
					/>
					<VisuallyHidden id="filterByTitleDescription">
						{formatMessage(i18n.textFilterDescription)}
					</VisuallyHidden>
					<Pills pill={pill} setPill={setPill} pills={pills} />
				</div>
				<PageCardAppearanceToggle
					appearance={appearance}
					setAppearance={setAppearance}
					disabled={loading}
					source={`${selectedView}Screen`}
				/>
				{queryError && <ErrorDisplay error={queryError} />}
				{mutationError && <ErrorDisplay error={mutationError} />}
			</div>
		);
	}

	return (
		<OptionsContainer>
			<OptionsLeft>
				<Textfield
					placeholder={placeholder}
					aria-label={placeholder}
					defaultValue={filterText}
					isCompact
					width="medium"
					onChange={onSearchChange}
					onClick={onClick}
					elemBeforeInput={<SearchIcon label="" spacing="spacious" />}
					aria-describedby="filterByTitleDescription"
				/>
				<VisuallyHidden id="filterByTitleDescription">
					{formatMessage(i18n.textFilterDescription)}
				</VisuallyHidden>
				<Pills pill={pill} setPill={setPill} pills={pills} />
			</OptionsLeft>
			<PageCardAppearanceToggle
				appearance={appearance}
				setAppearance={setAppearance}
				disabled={loading}
				source={`${selectedView}Screen`}
			/>
			{queryError && <ErrorDisplay error={queryError} />}
			{mutationError && <ErrorDisplay error={mutationError} />}
		</OptionsContainer>
	);
};
